import React, { Suspense } from 'react';
import { lazyPage } from './lazyPage';
import { Routes, Route, Navigate } from 'react-router-dom';
import RouteLoadingFallback from '../components/routing/RouteLoadingFallback';
import ProtectedRoute from '../components/ProtectedRoute';
import MainLayout from '../components/layout/MainLayout';
import FisioterapiaHiddenRoute from '../components/routing/FisioterapiaHiddenRoute';

// Páginas de autenticação
import Login from '../pages/Auth/Login';

// Páginas principais (protegidas)
const Dashboard = lazyPage(() => import('../pages/Dashboard'));
const CrmBoard = lazyPage(() => import('../pages/CrmBoard'));
const CrmTreatmentReport = lazyPage(() => import('../pages/CrmTreatmentReport'));
const CrmConfig = lazyPage(() => import('../pages/CrmConfig'));
const CrmDailyPanel = lazyPage(() => import('../pages/CrmDailyPanel'));
const CrmCampaigns = lazyPage(() => import('../pages/CrmCampaigns'));
const Campaigns = lazyPage(() => import('../pages/Campaigns'));
const DisparosList = lazyPage(() => import('../pages/Campaigns/DisparosList'));
const DisparosCreate = lazyPage(() => import('../pages/Campaigns/DisparosCreate'));
const DisparosEdit = lazyPage(() => import('../pages/Campaigns/DisparosEdit'));
const Sms = lazyPage(() => import('../pages/Campaigns/Sms'));
const SmsList = lazyPage(() => import('../pages/Campaigns/SmsList'));
const VoiceCreate = lazyPage(() => import('../pages/Campaigns/VoiceCreate'));
const Followups = lazyPage(() => import('../pages/Followups'));
const Inbox = lazyPage(() => import('../pages/Inbox'));
const WhatsApp = lazyPage(() => import('../pages/Inbox/WhatsApp'));
const WhatsAppRecovery = lazyPage(() => import('../pages/Inbox/WhatsAppRecovery'));
const DynamicWhatsAppInbox = lazyPage(() => import('../pages/Inbox/DynamicWhatsAppInbox'));
const Telegram = lazyPage(() => import('../pages/Inbox/Telegram'));
const TelegramDashboard = lazyPage(() => import('../pages/TelegramDashboard'));
const Instagram = lazyPage(() => import('../pages/Inbox/Instagram'));
const TeamChat = lazyPage(() => import('../pages/TeamChat/TeamChat'));
const Executions = lazyPage(() => import('../pages/Executions'));
const Settings = lazyPage(() => import('../pages/Settings'));
const CallLogs = lazyPage(() => import('../pages/CallLogs'));
const CallTemplateManager = lazyPage(() => import('../components/CallTemplateManager'));
const Agenda = lazyPage(() => import('../pages/Agenda'));
const WhatsAppAuto = lazyPage(() => import('../pages/WhatsAppAuto'));
const WhatsAppQR = lazyPage(() => import('../pages/WhatsAppQR'));
const WhatsAppManual = lazyPage(() => import('../pages/WhatsAppManual'));
const WhatsAppDashboard = lazyPage(() => import('../pages/WhatsAppDashboard'));
const WhatsAppProxyConfig = lazyPage(() => import('../pages/WhatsAppProxyConfig'));
const MessageQueue = lazyPage(() => import('../pages/WhatsAppAuto/MessageQueue'));
const WhatsAppCloudApi = lazyPage(() => import('../pages/WhatsAppCloudApi'));
const ClientManagement = lazyPage(() => import('../pages/ClientManagement'));
const QuizFunnelTracker = lazyPage(() => import('../pages/QuizFunnelTracker'));
const FacebookOAuthCallback = lazyPage(() => import('../pages/FacebookOAuthCallback'));
const EmbeddedSignupLauncher = lazyPage(() => import('../pages/EmbeddedSignupLauncher'));
const FacebookAdsDashboard = lazyPage(() => import('../pages/FacebookAds/Dashboard'));
const FacebookAdsCampaigns = lazyPage(() => import('../pages/FacebookAds/Campaigns'));
const FacebookAdsAdSets = lazyPage(() => import('../pages/FacebookAds/AdSets'));
const FacebookAdsAds = lazyPage(() => import('../pages/FacebookAds/Ads'));
const FacebookAdsBulkOperations = lazyPage(() => import('../pages/FacebookAds/BulkOperations'));
const FacebookAdsLeads = lazyPage(() => import('../pages/FacebookAds/Leads'));
const FacebookAdsConnections = lazyPage(() => import('../pages/FacebookAds/Connections'));
const FinancialControlDashboard = lazyPage(() => import('../pages/FinancialControl/Dashboard'));
const FinancialControlProjects = lazyPage(() => import('../pages/FinancialControl/Projects'));
const FinancialControlRevenue = lazyPage(() => import('../pages/FinancialControl/Revenue'));
const FinancialControlExpenses = lazyPage(() => import('../pages/FinancialControl/Expenses'));
const Kanban = lazyPage(() => import('../pages/Kanban'));
const BoardView = lazyPage(() => import('../pages/Kanban/BoardView'));
const Integrations = lazyPage(() => import('../pages/Integrations'));
const IntegrationMetrics = lazyPage(() => import('../pages/IntegrationMetrics'));
const ControlDashboard = lazyPage(() => import('../pages/Control/ControlDashboard'));
const SupportDashboard = lazyPage(() => import('../pages/Support/SupportDashboard'));
const AssistantCentralPage = lazyPage(() => import('../pages/Support/AssistantCentralPage'));
const AbandonedCartDashboard = lazyPage(() => import('../pages/AbandonedCart/AbandonedCartDashboard'));
const EmailCampaigns = lazyPage(() => import('../pages/EmailCampaigns'));
const EmailTemplates = lazyPage(() => import('../pages/EmailTemplates'));
const EmailAutomations = lazyPage(() => import('../pages/EmailAutomations'));
const SmsAutomations = lazyPage(() => import('../pages/SmsAutomations'));
const SmsTemplates = lazyPage(() => import('../pages/SmsTemplates'));
const Attendants = lazyPage(() => import('../pages/Attendants'));
const AttendantsDashboard = lazyPage(() => import('../pages/AttendantsDashboard'));

// Páginas da Clínica
const Patients = lazyPage(() => import('../pages/Clinic/Patients'));
const Appointments = lazyPage(() => import('../pages/Clinic/Appointments'));
const MedicalRecords = lazyPage(() => import('../pages/Clinic/MedicalRecords'));
const Evolutions = lazyPage(() => import('../pages/Clinic/Evolutions'));
const PhysiotherapyAssessments = lazyPage(() => import('../pages/Clinic/PhysiotherapyAssessments'));
const MedicalReports = lazyPage(() => import('../pages/Clinic/MedicalReports'));
const Procedures = lazyPage(() => import('../pages/Clinic/Procedures'));
const PatientPlans = lazyPage(() => import('../pages/Clinic/PatientPlans'));
const PatientSources = lazyPage(() => import('../pages/Clinic/PatientSources'));
const Professionals = lazyPage(() => import('../pages/Clinic/Professionals'));
const Inventory = lazyPage(() => import('../pages/Clinic/Inventory'));
const Budgets = lazyPage(() => import('../pages/Clinic/Budgets'));
const ClinicSettings = lazyPage(() => import('../pages/Clinic/ClinicSettings'));
const Prescriptions = lazyPage(() => import('../pages/Clinic/Prescriptions'));
const MemedPrescriptionWorkspace = lazyPage(() => import('../pages/Clinic/MemedPrescriptionWorkspace'));
const ExamOrders = lazyPage(() => import('../pages/Clinic/ExamOrders'));
const Laboratory = lazyPage(() => import('../pages/Clinic/Laboratory'));
const Certificates = lazyPage(() => import('../pages/Clinic/Certificates'));
const Referrals = lazyPage(() => import('../pages/Clinic/Referrals'));
const Receipts = lazyPage(() => import('../pages/Clinic/Receipts'));
const NFSe = lazyPage(() => import('../pages/Clinic/NFSe'));
const InsuranceManagement = lazyPage(() => import('../pages/Clinic/InsuranceManagement'));
const WaitingRoom = lazyPage(() => import('../pages/Clinic/WaitingRoom'));
const ImageGallery = lazyPage(() => import('../pages/Clinic/ImageGallery'));
const TreatmentPlan = lazyPage(() => import('../pages/Clinic/TreatmentPlan'));
const PlanningTemplateSettings = lazyPage(() => import('../pages/Clinic/PlanningTemplateSettings'));
const PatientPlannings = lazyPage(() => import('../pages/Clinic/PatientPlannings'));
const Odontogram = lazyPage(() => import('../pages/Clinic/Odontogram'));
const DocumentSigning = lazyPage(() => import('../pages/Clinic/DocumentSigning'));
const ContractTemplates = lazyPage(() => import('../pages/Clinic/ContractTemplates'));
const PublicSignPage = lazyPage(() => import('../pages/PublicSignature/PublicSignPage'));
const PublicValidatePage = lazyPage(() => import('../pages/PublicSignature/PublicValidatePage'));
const PeriodontalCharts = lazyPage(() => import('../pages/Clinic/PeriodontalCharts'));
const DentalTreatments = lazyPage(() => import('../pages/Clinic/DentalTreatments'));
const Reports = lazyPage(() => import('../pages/Clinic/Reports'));
const MetaLeadsReport = lazyPage(() => import('../pages/Clinic/MetaLeadsReport'));
const DailyAudit = lazyPage(() => import('../pages/Clinic/DailyAudit'));
const AnamnesisTemplates = lazyPage(() => import('../pages/Clinic/AnamnesisTemplates'));
const ReminderSettings = lazyPage(() => import('../pages/Clinic/ReminderSettings'));
const Birthdays = lazyPage(() => import('../pages/Clinic/Birthdays'));
const DigitalSignature = lazyPage(() => import('../pages/Clinic/DigitalSignature'));
const Doctoralia = lazyPage(() => import('../pages/Clinic/Doctoralia'));
const Tutoriais = lazyPage(() => import('../pages/Tutoriais'));
const UserManagement = lazyPage(() => import('../pages/UserManagement'));
const PlatformPlanPayments = lazyPage(() => import('../pages/PlatformPlanPayments'));
const PlatformClients = lazyPage(() => import('../pages/PlatformClients'));
const PlatformCrmForms = lazyPage(() => import('../pages/PlatformCrmForms'));
const PlatformSupportLayout = lazyPage(() => import('../pages/platformSupport/PlatformSupportLayout'));
const PlatformSupportInboxRedirect = lazyPage(() => import('../pages/platformSupport/PlatformSupportInboxRedirect'));
const AdminUnits = lazyPage(() => import('../pages/Admin/Units'));

// Teleconsulta
const PatientTeleconsultPage = lazyPage(() => import('../pages/Teleconsulta/PatientTeleconsultPage'));
const ProfessionalTeleconsultPage = lazyPage(() => import('../pages/Teleconsulta/ProfessionalTeleconsultPage'));

// Páginas do Financeiro
const FinancialDashboard = lazyPage(() => import('../pages/Financial/FinancialDashboard'));
const AccountsPayable = lazyPage(() => import('../pages/Financial/AccountsPayable'));
const NfeReceived = lazyPage(() => import('../pages/Financial/NfeReceived'));
const AccountsReceivable = lazyPage(() => import('../pages/Financial/AccountsReceivable'));
const CashRegister = lazyPage(() => import('../pages/Financial/CashRegister'));
const FinancialAudit = lazyPage(() => import('../pages/Financial/FinancialAudit'));
const BankStatements = lazyPage(() => import('../pages/Financial/BankStatements'));
const Repasses = lazyPage(() => import('../pages/Financial/Repasses'));
const CommissionRules = lazyPage(() => import('../pages/Clinic/Commissions'));
const TefTransactions = lazyPage(() => import('../pages/Financial/TefTransactions'));
const NfseCompanies = lazyPage(() => import('../pages/Financial/NfseCompanies'));
const ChecksReceived = lazyPage(() => import('../pages/Financial/ChecksReceived'));
const ChecksIssued = lazyPage(() => import('../pages/Financial/ChecksIssued'));
const CardSettlements = lazyPage(() => import('../pages/Financial/CardReceivables'));
const InsuranceReceivables = lazyPage(() => import('../pages/Financial/InsuranceReceivables'));
const PendingDiscounts = lazyPage(() => import('../pages/Financial/PendingDiscounts'));
const ProfessionalFees = lazyPage(() => import('../pages/Financial/ProfessionalFees'));
const Dre = lazyPage(() => import('../pages/Financial/Dre'));
const ChartOfAccounts = lazyPage(() => import('../pages/Financial/ChartOfAccounts'));
const DreSnapshots = lazyPage(() => import('../pages/Financial/DreSnapshots'));

// Páginas do Estoque
const InventoryMain = lazyPage(() => import('../pages/Inventory/InventoryMain'));
const InventoryItemDetail = lazyPage(() => import('../pages/Inventory/InventoryItemDetail'));
const InventoryCategories = lazyPage(() => import('../pages/Inventory/InventoryCategories'));
const InventoryAttributes = lazyPage(() => import('../pages/Inventory/InventoryAttributes'));
const InventoryProductLines = lazyPage(() => import('../pages/Inventory/InventoryProductLines'));
const InventoryLocations = lazyPage(() => import('../pages/Inventory/InventoryLocations'));
const InventoryManufacturers = lazyPage(() => import('../pages/Inventory/InventoryManufacturers'));
const InventorySuppliers = lazyPage(() => import('../pages/Inventory/InventorySuppliers'));
const InventoryKits = lazyPage(() => import('../pages/Inventory/InventoryKits'));
const InsuranceMedications = lazyPage(() => import('../pages/Inventory/InsuranceMedications'));
const InventoryRequisitions = lazyPage(() => import('../pages/Inventory/InventoryRequisitions'));
const InventoryConsignedPanel = lazyPage(() => import('../pages/Inventory/InventoryConsignedPanel'));
const InventoryOperationsPanel = lazyPage(() => import('../pages/Inventory/InventoryOperationsPanel'));
const InventoryPendingPanel = lazyPage(() => import('../pages/Inventory/InventoryPendingPanel'));

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback fullScreen />}>
    <Routes>
      {/* Rota pública - Login */}
      <Route path="/login" element={<Login />} />
      <Route path="/facebook/callback" element={<FacebookOAuthCallback />} />
      <Route path="/embedded-signup-launcher" element={<EmbeddedSignupLauncher />} />
      {/* Teleconsulta pública (paciente) — sem autenticação */}
      <Route path="/teleconsulta/p/:token" element={<PatientTeleconsultPage />} />
      {/* Assinatura de contrato pelo paciente — sem autenticação.
          O caminho precisa casar com SIGNATURE_PUBLIC_PATH no backend, porque é
          ele que está fixo no botão do template aprovado na Meta.
          `validar` vem antes porque `:token` casaria com ela. */}
      <Route path="/assinar/validar/:envelopeId" element={<PublicValidatePage />} />
      <Route path="/assinar/:token" element={<PublicSignPage />} />

      {/* Memed em aba dedicada — sem menu lateral, para não bloquear o Klinvia na aba original */}
      <Route
        path="/clinic/memed-prescribe"
        element={
          <ProtectedRoute>
            <MemedPrescriptionWorkspace />
          </ProtectedRoute>
        }
      />

      {/* Layout principal com menu lateral para todas as páginas protegidas */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Rota raiz - redirecionar para agenda */}
        <Route index element={<Navigate to="/agenda" replace />} />
        
        {/* Rotas protegidas com menu lateral */}
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="crm" element={<CrmBoard />} />
        <Route path="crm/config" element={<CrmConfig />} />
        <Route path="crm/panel" element={<CrmDailyPanel />} />
        <Route path="crm/campaigns" element={<CrmCampaigns />} />
        <Route path="crm/treatment-report" element={<CrmTreatmentReport />} />
        {/* CRM unificado: rota legada redireciona para o quadro novo */}
        <Route path="crm/leads" element={<Navigate to="/crm" replace />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="campaigns/disparos" element={<DisparosList />} />
        <Route path="campaigns/disparos/create" element={<DisparosCreate />} />
        <Route path="campaigns/disparos/edit/:id" element={<DisparosEdit />} />
        <Route path="campaigns/sms" element={<SmsList />} />
        <Route path="campaigns/sms/create" element={<Sms />} />
        <Route path="campaigns/voice" element={<VoiceCreate />} />
        <Route path="campaigns/email-campaigns" element={<EmailCampaigns />} />
        <Route path="campaigns/email-templates" element={<EmailTemplates />} />
        <Route path="automations/email" element={<EmailAutomations />} />
        <Route path="automations/sms" element={<SmsAutomations />} />
        <Route path="automations/sms-templates" element={<SmsTemplates />} />
        <Route path="followups" element={<Followups />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="inbox/whatsapp" element={<WhatsApp />} />
        <Route path="inbox/whatsapp-recovery" element={<WhatsAppRecovery />} />
        <Route path="inbox/telegram" element={<Telegram />} />
        <Route path="telegram-dashboard" element={<TelegramDashboard />} />
        <Route path="inbox/instagram" element={<Instagram />} />
        {/* Rota dinâmica para inboxes adicionais configurados via variáveis de ambiente */}
        <Route path="inbox/:inboxSlug" element={<DynamicWhatsAppInbox />} />
        <Route path="team-chat" element={<TeamChat />} />
        <Route path="executions" element={<Executions />} />
        <Route path="call-logs" element={<CallLogs />} />
        <Route path="call-templates" element={<CallTemplateManager />} />
        <Route path="agenda" element={<Agenda expandedLayout />} />
        <Route path="agenda-expandida" element={<Navigate to="/agenda" replace />} />
        <Route path="whatsapp-auto" element={<WhatsAppAuto />} />
        <Route path="whatsapp-auto/queue" element={<MessageQueue />} />
        <Route path="whatsapp-manual" element={<WhatsAppManual />} />
        <Route path="whatsapp-qr" element={<WhatsAppQR />} />
        <Route path="whatsapp-dashboard" element={<WhatsAppDashboard />} />
        <Route path="whatsapp-proxy" element={<WhatsAppProxyConfig />} />
        <Route path="whatsapp-cloud-api" element={<WhatsAppCloudApi />} />
        <Route path="client-management" element={<ClientManagement />} />
        <Route path="quiz-funnel" element={<QuizFunnelTracker />} />
        <Route path="facebook-ads" element={<FacebookAdsDashboard />} />
        <Route path="facebook-ads/campaigns" element={<FacebookAdsCampaigns />} />
        <Route path="facebook-ads/adsets" element={<FacebookAdsAdSets />} />
        <Route path="facebook-ads/ads" element={<FacebookAdsAds />} />
        <Route path="facebook-ads/bulk" element={<FacebookAdsBulkOperations />} />
        <Route path="facebook-ads/leads" element={<FacebookAdsLeads />} />
        <Route path="facebook-ads/connections" element={<FacebookAdsConnections />} />
        <Route path="financial-control" element={<FinancialControlDashboard />} />
        <Route path="financial-control/projects" element={<FinancialControlProjects />} />
        <Route path="financial-control/revenue" element={<FinancialControlRevenue />} />
        <Route path="financial-control/expenses" element={<FinancialControlExpenses />} />
        <Route path="kanban" element={<Kanban />} />
        <Route path="kanban/:boardId" element={<BoardView />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="integrations/:provider/metrics" element={<IntegrationMetrics />} />
        <Route path="control" element={<ControlDashboard />} />
        <Route path="support" element={<SupportDashboard />} />
        <Route path="central-ia" element={<AssistantCentralPage />} />
        <Route path="abandoned-cart" element={<AbandonedCartDashboard />} />
        <Route path="attendants" element={<Attendants />} />
        <Route path="attendants/dashboard" element={<AttendantsDashboard />} />
        
        {/* Rotas da Clínica — /clinic apenas redireciona (dashboard dedicado foi removido) */}
        <Route path="clinic" element={<Navigate to="/agenda" replace />} />
        <Route path="clinic/patients" element={<Patients />} />
        <Route path="clinic/appointments" element={<Appointments />} />
        <Route path="clinic/medical-records" element={<MedicalRecords />} />
        <Route path="clinic/evolutions" element={<Evolutions />} />
        <Route path="clinic/physiotherapy-assessments" element={<PhysiotherapyAssessments />} />
        <Route path="clinic/medical-reports" element={<MedicalReports />} />
        <Route path="clinic/procedures" element={<Procedures />} />
        <Route path="clinic/patient-plans" element={<PatientPlans />} />
        <Route path="clinic/patient-sources" element={<PatientSources />} />
        <Route path="clinic/professionals" element={<Professionals />} />
        <Route path="clinic/inventory" element={<Inventory />} />
        <Route path="clinic/budgets" element={<Budgets />} />
        <Route path="clinic/prescriptions" element={<FisioterapiaHiddenRoute><Prescriptions /></FisioterapiaHiddenRoute>} />
        <Route path="clinic/exam-orders" element={<FisioterapiaHiddenRoute><ExamOrders /></FisioterapiaHiddenRoute>} />
        <Route path="clinic/settings" element={<ClinicSettings />} />
        <Route path="clinic/laboratory" element={<FisioterapiaHiddenRoute><Laboratory /></FisioterapiaHiddenRoute>} />
        <Route path="clinic/certificates" element={<FisioterapiaHiddenRoute><Certificates /></FisioterapiaHiddenRoute>} />
        <Route path="clinic/referrals" element={<Referrals />} />
        <Route path="clinic/insurance" element={<InsuranceManagement />} />
        <Route path="clinic/waiting-room" element={<WaitingRoom />} />
        <Route path="clinic/image-gallery" element={<ImageGallery />} />
        <Route path="clinic/treatment-plan" element={<TreatmentPlan />} />
        <Route path="clinic/plannings" element={<PatientPlannings />} />
        <Route path="clinic/planning-templates" element={<PlanningTemplateSettings />} />
        <Route path="clinic/odontogram" element={<Odontogram />} />
        <Route path="clinic/document-signing" element={<DocumentSigning />} />
        <Route path="clinic/contract-templates" element={<ContractTemplates />} />
        <Route path="clinic/periodontal-charts" element={<PeriodontalCharts />} />
        <Route path="clinic/dental-treatments" element={<DentalTreatments />} />
        <Route path="clinic/reports" element={<Reports />} />
        <Route path="clinic/reports/meta-leads" element={<MetaLeadsReport />} />
        <Route path="clinic/daily-audit" element={<DailyAudit />} />
        <Route path="clinic/anamnesis-templates" element={<AnamnesisTemplates />} />
        <Route path="clinic/reminder-settings" element={<ReminderSettings />} />
        <Route path="clinic/birthdays" element={<Birthdays />} />
        <Route path="clinic/digital-signature" element={<DigitalSignature />} />
        <Route path="clinic/doctoralia" element={<Doctoralia />} />
        <Route path="tutoriais" element={<Tutoriais />} />
        <Route path="clinic/teleconsulta/:appointmentId" element={<ProfessionalTeleconsultPage />} />
        
        {/* Rotas do Financeiro */}
        <Route path="financial/dashboard" element={<FinancialDashboard />} />
        <Route path="financial/accounts-payable" element={<AccountsPayable />} />
        <Route path="financial/nfe-received" element={<NfeReceived />} />
        <Route path="financial/accounts-receivable" element={<AccountsReceivable />} />
        <Route path="financial/cash-register" element={<CashRegister />} />
        <Route path="financial/audit" element={<FinancialAudit />} />
        <Route path="financial/bank-statements" element={<BankStatements />} />
        <Route path="financial/repasses" element={<Repasses />} />
        <Route path="financial/commission-rules" element={<CommissionRules />} />
        <Route path="financial/tef" element={<TefTransactions />} />
        <Route path="financial/nfse" element={<NFSe />} />
        <Route path="financial/nfse-companies" element={<NfseCompanies />} />
        <Route path="financial/receipts" element={<Receipts />} />
        <Route path="financial/checks-received" element={<ChecksReceived />} />
        <Route path="financial/checks-issued" element={<ChecksIssued />} />
        <Route path="financial/card-settlements" element={<CardSettlements />} />
        <Route path="financial/insurance-receivables" element={<InsuranceReceivables />} />
        <Route path="financial/pending-discounts" element={<PendingDiscounts />} />
        <Route path="financial/professional-fees" element={<ProfessionalFees />} />
        <Route path="financial/dre" element={<Dre />} />
        <Route path="financial/dre/snapshots" element={<DreSnapshots />} />
        <Route path="financial/chart-of-accounts" element={<ChartOfAccounts />} />

        {/* Rotas do Estoque */}
        <Route path="inventory/items" element={<InventoryMain />} />
        <Route path="inventory/items/:itemId" element={<InventoryItemDetail />} />
        <Route path="inventory/categories" element={<InventoryCategories />} />
        <Route path="inventory/attributes" element={<InventoryAttributes />} />
        <Route path="inventory/product-lines" element={<InventoryProductLines />} />
        <Route path="inventory/locations" element={<InventoryLocations />} />
        <Route path="inventory/manufacturers" element={<InventoryManufacturers />} />
        <Route path="inventory/suppliers" element={<InventorySuppliers />} />
        <Route path="inventory/kits" element={<InventoryKits />} />
        <Route path="inventory/insurance-medications" element={<InsuranceMedications />} />
        <Route path="inventory/movements" element={<Navigate to="/inventory/items?tab=movimentacoes" replace />} />
        <Route path="inventory/requisitions" element={<InventoryRequisitions />} />
        <Route path="inventory/consigned" element={<InventoryConsignedPanel />} />
        <Route path="inventory/operations" element={<InventoryOperationsPanel />} />
        <Route path="inventory/pending" element={<InventoryPendingPanel />} />
        
        <Route path="settings" element={<Settings />} />
        
        {/* Gestão de Usuários (admin) */}
        <Route path="user-management" element={<UserManagement />} />
        <Route path="platform-payments" element={<PlatformPlanPayments />} />
        {/* Painel central da Klinvia (deploy interno, PLATFORM_ADMIN_ENABLED=true) */}
        <Route path="platform-clients" element={<PlatformClients />} />
        <Route path="platform-support" element={<PlatformSupportLayout />}>
          <Route index element={<PlatformSupportInboxRedirect />} />
          <Route path="inbox/:inboxSlug" element={<DynamicWhatsAppInbox />} />
        </Route>
        <Route path="platform-crm-forms" element={<PlatformCrmForms />} />

        {/* Administração - Unidades (admin) */}
        <Route path="admin/units" element={<AdminUnits />} />
        
        {/* Rota para qualquer caminho não encontrado - redirecionar para agenda */}
        <Route path="*" element={<Navigate to="/agenda" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
};

export default AppRoutes; 